import OutputDeviceCollection from '@twilio/voice-sdk/es5/twilio/outputdevicecollection';
import { Reducer } from 'react';

export type ClosedState = { status: 'closed' };
export type ConnectingState = { status: 'connecting' } & CalleeData & AudioData;
export type OpenState = { status: 'open' } & OpenData & AudioData;
export type ReconnectingState = { status: 'reconnecting' } & OpenData &
  AudioData;
export type RecordingDisclaimerState = {
  status: 'recordingDisclaimer';
} & OpenData &
  AudioData;

type CalleeData = {
  callee: {
    id: number;
    firstName?: string;
    lastName?: string;
    website?: string;
    phoneNumber: string;
  };
};

export type OpenData = {
  callStart: Date;
  isMuted: boolean;
  isDialpadOpen: boolean;
  isRecording: boolean;
} & CalleeData;

export type AudioData = {
  areSettingsOpen: boolean;
  availableInputDevices?: Map<string, MediaDeviceInfo>;
  availableOutputDevices?: Map<string, MediaDeviceInfo>;
  selectedInputDevice?: MediaDeviceInfo;
  activeSpeakerDevices?: OutputDeviceCollection;
};

export type CronoCallState =
  | ClosedState
  | ConnectingState
  | OpenState
  | ReconnectingState
  | RecordingDisclaimerState;

export type CronoCallEvent =
  | ({ type: 'callConnecting' } & CalleeData & AudioData)
  | { type: 'callAccepted'; callStart: Date }
  | { type: 'callDisconnected' }
  | { type: 'callCanceled' }
  | { type: 'callMuted'; isMuted: boolean }
  | { type: 'openDialpad'; shouldOpen: boolean }
  | { type: 'callReconnected' }
  | { type: 'callReconnecting' }
  | { type: 'callRecordingRequested' }
  | { type: 'callRecordingStarted' }
  | { type: 'callRecordingCanceled' }
  | { type: 'openSettings'; shouldOpen: boolean }
  | {
      type: 'selectedInputDeviceChanged';
      selectedInputDevice?: MediaDeviceInfo;
    }
  | {
      type: 'activeSpeakerDevicesChanged';
      activeSpeakerDevices?: OutputDeviceCollection;
    }
  | {
      type: 'availableInputDevicesChanged';
      availableInputDevices?: Map<string, MediaDeviceInfo>;
    }
  | {
      type: 'availableOutputDevicesChanged';
      availableOutputDevices?: Map<string, MediaDeviceInfo>;
    }
  | { type: 'callErrored' };

const closedReducer = (
  state: ClosedState,
  event: CronoCallEvent,
): CronoCallState => {
  switch (event.type) {
    case 'callConnecting':
      return {
        status: 'connecting',
        callee: event.callee,
        availableInputDevices: event.availableInputDevices,
        availableOutputDevices: event.availableOutputDevices,
        selectedInputDevice: event.selectedInputDevice,
        activeSpeakerDevices: event.activeSpeakerDevices,
        areSettingsOpen: event.areSettingsOpen,
      };
    default:
      return state;
  }
};

const connectingReducer = (
  state: ConnectingState,
  event: CronoCallEvent,
): CronoCallState => {
  switch (event.type) {
    case 'callAccepted':
      return {
        ...state,
        status: 'open',
        isMuted: false,
        isDialpadOpen: false,
        isRecording: false,
        callStart: event.callStart,
      };
    case 'callDisconnected':
    case 'callCanceled':
      return { status: 'closed' };
    case 'openSettings':
      return { ...state, areSettingsOpen: event.shouldOpen };
    case 'selectedInputDeviceChanged':
      return { ...state, selectedInputDevice: event.selectedInputDevice };
    case 'activeSpeakerDevicesChanged':
      return {
        ...state,
        activeSpeakerDevices: event.activeSpeakerDevices,
      };
    case 'availableInputDevicesChanged':
      return {
        ...state,
        availableInputDevices: event.availableInputDevices,
      };
    case 'availableOutputDevicesChanged':
      return {
        ...state,
        availableOutputDevices: event.availableOutputDevices,
      };
    case 'callErrored':
      return { status: 'closed' };
    default:
      return state;
  }
};

const openReducer = (
  state: OpenState,
  event: CronoCallEvent,
): CronoCallState => {
  switch (event.type) {
    case 'callDisconnected':
    case 'callCanceled':
      return { status: 'closed' };
    case 'callReconnecting':
      return { ...state, status: 'reconnecting' };
    case 'callMuted':
      return { ...state, isMuted: event.isMuted };
    case 'openDialpad':
      return { ...state, isDialpadOpen: event.shouldOpen };
    case 'callRecordingRequested':
      if (state.isRecording) return state;
      return { ...state, status: 'recordingDisclaimer' };
    case 'openSettings':
      return { ...state, areSettingsOpen: event.shouldOpen };
    case 'selectedInputDeviceChanged':
      return { ...state, selectedInputDevice: event.selectedInputDevice };
    case 'activeSpeakerDevicesChanged':
      return {
        ...state,
        activeSpeakerDevices: event.activeSpeakerDevices,
      };
    case 'availableInputDevicesChanged':
      return {
        ...state,
        availableInputDevices: event.availableInputDevices,
      };
    case 'availableOutputDevicesChanged':
      return {
        ...state,
        availableOutputDevices: event.availableOutputDevices,
      };
    case 'callErrored':
      return { status: 'closed' };
    default:
      return state;
  }
};

const reconnectingReducer = (
  state: ReconnectingState,
  event: CronoCallEvent,
): CronoCallState => {
  switch (event.type) {
    case 'callDisconnected':
    case 'callCanceled':
      return { status: 'closed' };
    case 'callReconnected':
      return {
        ...state,
        status: 'open',
      };
    case 'openSettings':
      return { ...state, areSettingsOpen: event.shouldOpen };
    case 'selectedInputDeviceChanged':
      return { ...state, selectedInputDevice: event.selectedInputDevice };
    case 'activeSpeakerDevicesChanged':
      return {
        ...state,
        activeSpeakerDevices: event.activeSpeakerDevices,
      };
    case 'availableInputDevicesChanged':
      return {
        ...state,
        availableInputDevices: event.availableInputDevices,
      };
    case 'availableOutputDevicesChanged':
      return {
        ...state,
        availableOutputDevices: event.availableOutputDevices,
      };
    case 'callErrored':
      return { status: 'closed' };
    default:
      return state;
  }
};

const recordingDisclaimerReducer = (
  state: RecordingDisclaimerState,
  event: CronoCallEvent,
): CronoCallState => {
  switch (event.type) {
    case 'callDisconnected':
    case 'callCanceled':
      return { status: 'closed' };
    case 'callRecordingCanceled':
      return {
        ...state,
        status: 'open',
      };
    case 'callRecordingStarted':
      return {
        ...state,
        status: 'open',
        isRecording: true,
      };
    case 'availableInputDevicesChanged':
      return {
        ...state,
        availableInputDevices: event.availableInputDevices,
      };
    case 'availableOutputDevicesChanged':
      return {
        ...state,
        availableOutputDevices: event.availableOutputDevices,
      };
    case 'callErrored':
      return { status: 'closed' };
    default:
      return state;
  }
};

// Crono VoIP reducer implemented as a Finite State Machine (FSM).
// We only define the transitions that are valid from a given state.
export const cronoCallReducer: Reducer<CronoCallState, CronoCallEvent> = (
  state,
  event,
) => {
  switch (state.status) {
    case 'closed':
      return closedReducer(state, event);
    case 'connecting':
      return connectingReducer(state, event);
    case 'open':
      return openReducer(state, event);
    case 'reconnecting':
      return reconnectingReducer(state, event);
    case 'recordingDisclaimer':
      return recordingDisclaimerReducer(state, event);
    default:
      return state;
  }
};
